/**
 * Gets Google payment data request
 *
 * @param {Object} dataRequest - data request
 * @returns {Object} paymentDataRequest - returns payment data request
 */
function getGooglePaymentDataRequest(dataRequest) {
    if (!dataRequest) {
        return null;
    }

    const {
        countryCode,
        merchantInfo,
        transactionInfo,
        baseRequest,
        baseCardPaymentMethod,
        tokenizationSpecification,
        shippingMethods
    } = dataRequest;

    const cardPaymentMethod = {
        tokenizationSpecification,
        ...baseCardPaymentMethod
    };

    const paymentDataRequest = {...baseRequest};
    paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];
    paymentDataRequest.transactionInfo = transactionInfo;
    paymentDataRequest.merchantInfo = merchantInfo;

    paymentDataRequest.callbackIntents = ['SHIPPING_ADDRESS', 'SHIPPING_OPTION', 'PAYMENT_AUTHORIZATION'];
    paymentDataRequest.shippingAddressRequired = true;
    paymentDataRequest.shippingAddressParameters = {
        allowedCountryCodes: [countryCode],
        phoneNumberRequired: true
    };

    paymentDataRequest.shippingOptionRequired = true;
    paymentDataRequest.shippingOptionParameters = getShippingOptions(shippingMethods);
    paymentDataRequest.emailRequired = true;

    return paymentDataRequest;
}

/**
 * Gets shipping methods without amount attribute
 *
 * @param {Array} shippingMethods - shipping methods
 * @returns {Array} - shipping methods without amount attribute
 */
function getShippingMethodsWithoutAmount(shippingMethods) {
    const shippingMethodsCopy = shippingMethods.map(obj => ({...obj}));

    return shippingMethodsCopy.map(current => {
        delete current.amount;
        return current;
    });
}

/**
 * Gets Shipping Options
 *
 * @param {Array} shippingMethods - shipping methods
 * @returns {Object} - returns shipping options in the expected format by GooglePay
 */
function getShippingOptions(shippingMethods) {
    const shippingMethodsCopy = getShippingMethodsWithoutAmount(shippingMethods);

    return {
        defaultSelectedOptionId: shippingMethodsCopy[0].id,
        shippingOptions: shippingMethodsCopy
    };
}

/**
 * Gets shipping methods costs
 *
 * @param {Array} shippingMethods - shipping methods
 * @returns {Object} - returns an object whose key is the id of the shipping method and the value is the cost value.
 */
function getShippingMethodsCosts(shippingMethods) {
    const result = shippingMethods.reduce((acc, current) => {
        const {id, amount} = current;
        return {...acc, [id]: amount.toString()};
    }, {});
    return result;
}

/**
 * Calculates new transaction info
 *
 * @param {Object} transactionInfo - transaction Info
 * @param {Object} shippingMethods - shipping methods
 * @param {string} shippingOptionId - shipping option id
 * @returns {Object} newTransactionInfo - returns a new transaction info
 */
function calculateNewTransactionInfo(transactionInfo, shippingMethods, shippingOptionId) {
    const newTransactionInfo = transactionInfo;
    const shippingCost = getShippingMethodsCosts(shippingMethods)[shippingOptionId];

    newTransactionInfo.displayItems.push({
        type: 'LINE_ITEM',
        label: 'Shipping cost',
        price: shippingCost,
        status: 'FINAL'
    });

    return newTransactionInfo;
}

/**
 * Handles GooglePay response
 *
 * @param {Object} paymentData - paymentData
 */
function handleGooglePayResponse(paymentData) {
    const urlPaymentDataResponse = $('#googlepay-wrapper').data('url-payment-data-response');

    $.ajax({
        url: urlPaymentDataResponse,
        type: 'POST',
        dataType: 'json',
        data: {
            paymentData: JSON.stringify(paymentData)
        },
        /**
         * Asynchronous request success state
         *
         * @param {Object} data - data object
         */
        success: data => {
            if (data.isPDPContext) {
                restoreBasket();
            }
            if (data.placeOrderURL) {
                window.location.replace(data.placeOrderURL);
            } else {
                window.location.reload();
            }
        },
        /**
         * Asynchronous request error state
         *
         * @param {Object} err - error object
         */
        error: err => {
            restoreBasket();
            console.error(err);
            window.location.reload();
        }
    });
}

/**
 * Restores previous basket after one product purchase is complete on PDP
 *
 */
function restoreBasket() {
    const restoreBasketURL = $('#google-pay-pdp-button').data('restore-basket');

    if (!restoreBasketURL) {
        return;
    }

    $.ajax({
        url: restoreBasketURL,
        type: 'get',
        async: false
    });
}

module.exports = {
    getGooglePaymentDataRequest,
    getShippingOptions,
    calculateNewTransactionInfo,
    handleGooglePayResponse,
    restoreBasket
};
